import { Link } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import { useSEO } from "@/hooks/useSEO";
import { Card } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";

const Sitemap = () => {
  useSEO({
    title: "Sitemap | Ommni",
    description: "Complete sitemap of Ommni's website including all features, solutions, integrations, and resources.",
    keywords: "sitemap, ommni pages, site navigation, website structure"
  });

  const sitemapSections = [
    {
      category: "Main Pages",
      description: "Core pages and primary navigation",
      links: [
        { path: "/", label: "Home", description: "Unified commerce platform homepage" },
        { path: "/about", label: "About Us", description: "Learn about Ommni's mission and team" },
        { path: "/contact", label: "Contact", description: "Get in touch with our team" },
        { path: "/pricing", label: "Pricing", description: "Transparent pricing plans" },
        { path: "/free-trial", label: "Free Trial", description: "Start your free trial today" },
      ]
    },
    {
      category: "Features",
      description: "Powerful features to manage your commerce operations",
      links: [
        { path: "/features", label: "All Features", description: "Complete feature overview" },
        { path: "/features/pim", label: "PIM", description: "Product Information Management" },
        { path: "/features/dam", label: "DAM", description: "Digital Asset Management" },
        { path: "/features/dom", label: "DOM", description: "Distributed Order Management" },
        { path: "/features/inventory", label: "Inventory", description: "Real-time inventory management" },
        { path: "/features/offers", label: "Offers", description: "Pricing and promotion management" },
        { path: "/features/ommni-flow", label: "Ommni Flow", description: "No-code automation workflows" },
      ]
    },
    {
      category: "Solutions",
      description: "Industry-specific solutions tailored to your business",
      links: [
        { path: "/solutions/ecommerce", label: "Ecommerce", description: "E-commerce solutions" },
        { path: "/solutions/fashion", label: "Fashion & Apparel", description: "Fashion industry solutions" },
        { path: "/solutions/wholesale", label: "Wholesale & B2B", description: "Wholesale commerce solutions" },
        { path: "/solutions/manufacturing", label: "Manufacturing", description: "Manufacturing commerce solutions" },
      ]
    },
    {
      category: "Integrations",
      description: "Connect with your favorite tools and platforms",
      links: [
        { path: "/integrations", label: "All Integrations", description: "Browse 100+ integrations" },
        { path: "/integrations/shopify", label: "Shopify Integration", description: "Seamless Shopify connection" },
        { path: "/integrations/google-shopping", label: "Google Shopping", description: "Sync to Google Shopping" },
      ]
    },
    {
      category: "Resources",
      description: "Helpful resources and company information",
      links: [
        { path: "/blog", label: "Blog", description: "Latest insights and updates" },
        { path: "/support", label: "Support", description: "Get help and documentation" },
        { path: "/careers", label: "Careers", description: "Join our growing team" },
        { path: "/roadmap", label: "Product Roadmap", description: "Upcoming features and releases" },
      ]
    },
    {
      category: "Comparisons",
      description: "See how Ommni compares to alternatives",
      links: [
        { path: "/competitors/akeneo", label: "Ommni vs Akeneo", description: "Compare with Akeneo" },
        { path: "/competitors/pimberly", label: "Ommni vs Pimberly", description: "Compare with Pimberly" },
        { path: "/competitors/salsify", label: "Ommni vs Salsify", description: "Compare with Salsify" },
        { path: "/competitors/sales-layer", label: "Ommni vs Sales Layer", description: "Compare with Sales Layer" },
        { path: "/competitors/ultra-commerce", label: "Ommni vs UltraCommerce", description: "Compare with UltraCommerce" },
      ]
    },
    {
      category: "Special Pages",
      description: "Additional resources and tools",
      links: [
        { path: "/product360", label: "Product 360", description: "Complete product view solution" },
        { path: "/retail-tech-stack-2026", label: "Retail Tech Stack 2026", description: "Future of retail technology" },
      ]
    },
    {
      category: "Legal",
      description: "Legal information and policies",
      links: [
        { path: "/privacy", label: "Privacy Policy", description: "How we protect your data" },
        { path: "/terms", label: "Terms of Service", description: "Terms and conditions" },
      ]
    }
  ];

  return (
    <PageLayout>
      <div className="container mx-auto px-4 py-16 max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Sitemap</h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Navigate through all pages and features of the Ommni platform
          </p>
        </div>

        {/* Sitemap Grid */}
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {sitemapSections.map((section) => (
            <Card key={section.category} className="p-6 hover:shadow-lg transition-shadow">
              <h2 className="text-2xl font-bold mb-2">{section.category}</h2>
              <p className="text-sm text-muted-foreground mb-6">
                {section.description}
              </p>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="group flex items-start gap-2 hover:text-primary transition-colors"
                    >
                      <ArrowRight className="w-4 h-4 mt-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      <div className="flex-1">
                        <div className="font-medium group-hover:underline">
                          {link.label}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {link.description}
                        </div>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        {/* XML Sitemap Reference */}
        <div className="mt-16 text-center">
          <p className="text-sm text-muted-foreground">
            Looking for the XML sitemap for search engines?{" "}
            <a 
              href="/sitemap.xml" 
              className="text-primary hover:underline font-medium"
              target="_blank"
              rel="noopener noreferrer"
            >
              View XML Sitemap
            </a>
          </p>
        </div>
      </div>
    </PageLayout>
  );
};

export default Sitemap;
